import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { resolveApiAssetUrl } from '../lib/api';
import { HOOKS } from '../lib/domHooks';
import {
  isInternalLink, markWelcomeSeen, readWelcomeSeen, shouldShowWelcomePopup,
} from '../lib/welcomePopup';
import {
  clearJustRegistered, cmsAsWelcome, isMobileViewport, markPopupSeen, pickPopups, readJustRegistered,
  readPopupSeen,
} from '../lib/cmsPopups';
import RichHtmlFrame from './RichHtmlFrame';
import './WelcomePopup.css';

/**
 * Pencerenin görünümü. Panelin önizlemesi de bunu çizer — sitede ve panelde
 * aynı pencere görünmelidir.
 *
 * Metin **düz metindir** (`white-space: pre-line`); panelden gelen içerik HTML
 * olarak basılmaz (bkz. backend/services/welcome_popup.py).
 */
export const WelcomePopupCard = ({
  popup, onClose, onAction, scrollLayer = false,
}) => {
  const image = popup.image_url ? resolveApiAssetUrl(popup.image_url) : '';
  const hasButton = Boolean(popup.button_label && popup.button_url);
  const internal = isInternalLink(popup.button_url);
  const hasContent = Boolean(popup.title || popup.body || hasButton);

  const renderLink = (children, className, extra = {}) => {
    if (!hasButton) return null;
    if (internal) {
      return (
        <Link to={popup.button_url} className={className} onClick={onAction} {...extra}>
          {children}
        </Link>
      );
    }
    return (
      <a href={popup.button_url} target="_blank" rel="noopener noreferrer"
        className={className} onClick={onAction} {...extra}>
        {children}
      </a>
    );
  };

  const media = image ? (
    <img className="welcome-popup-image" src={image} alt={popup.title || ''} />
  ) : null;
  const inner = (
    <>
      {media && (hasButton
        ? renderLink(media, 'welcome-popup-media', { 'aria-label': popup.button_label })
        : <div className="welcome-popup-media">{media}</div>)}
      {hasContent && (
        <div className="welcome-popup-content">
          {popup.title && (
            <h2 id="welcome-popup-title" className="welcome-popup-title">{popup.title}</h2>
          )}
          {popup.body && <p className="welcome-popup-body">{popup.body}</p>}
          {renderLink(popup.button_label, 'welcome-popup-button gold-btn')}
        </div>
      )}
    </>
  );

  return (
    <div className="welcome-popup-dialog" onClick={(event) => event.stopPropagation()}>
      {onClose && (
        <button type="button" className="welcome-popup-close" onClick={onClose} aria-label="Kapat">
          <X size={18} />
        </button>
      )}
      {scrollLayer ? <div className="welcome-popup-scroll">{inner}</div> : inner}
    </div>
  );
};

/**
 * CMS › Açılır pencereler kaydı. Gövde HTML olabilir ve portalın DOM'una
 * basılmaz: sandbox çerçevesinde çizilir (bkz. RichHtmlFrame).
 */
export const CmsPopupCard = ({ popup, onClose }) => {
  const image = popup.image_url ? resolveApiAssetUrl(popup.image_url) : '';
  const link = String(popup.link || '');
  const internal = isInternalLink(link);
  const safeLink = internal || link.startsWith('https://') ? link : '';
  const media = image ? <img className="welcome-popup-image" src={image} alt={popup.alt_text || popup.title || ''} /> : null;
  let wrapped = media;
  if (media && safeLink) {
    wrapped = internal && !popup.new_window
      ? <Link to={safeLink} className="welcome-popup-media" onClick={onClose}>{media}</Link>
      : <a href={safeLink} className="welcome-popup-media" target="_blank" rel="noopener noreferrer" onClick={onClose}>{media}</a>;
  } else if (media) {
    wrapped = <div className="welcome-popup-media">{media}</div>;
  }
  return (
    <div className="welcome-popup-dialog" onClick={(event) => event.stopPropagation()} data-bhs-popup-id={popup.id}>
      <button type="button" className="welcome-popup-close" onClick={onClose} aria-label="Kapat"><X size={18} /></button>
      {wrapped}
      {(popup.title || popup.body) && (
        <div className="welcome-popup-content">
          {popup.title && <h2 id="welcome-popup-title" className="welcome-popup-title">{popup.title}</h2>}
          {popup.body && <RichHtmlFrame html={popup.body} title={popup.title || 'Duyuru'} />}
        </div>
      )}
    </div>
  );
};

/**
 * Yalnız görselden oluşan pencere (Betrun, 26.10.2026 sistem sahibi): görselin
 * üstünde kapatma düğmesi, başka çerçeve ya da düğme yok; görselin tamamı
 * bağlantıdır. Sınıf adları kasıtlı olarak `welcome-popup-dialog/-media/-image`
 * değildir: panel CSS'inin çerçeve, kırpma (16:8.4) ve rozet kuralları ile özel
 * JS'in eklediği "Daha sonra" / avantaj satırı bu pencereye uygulanmaz.
 */
export const PlainPopupCard = ({
  image, alt = '', link = '', newWindow = false, onClose, popupId, onLoad, onError,
}) => {
  const internal = isInternalLink(link);
  const safeLink = internal || String(link).startsWith('https://') ? link : '';
  const img = (
    <img
      className="welcome-popup-plain-image"
      src={resolveApiAssetUrl(image)}
      alt={alt}
      onLoad={onLoad}
      onError={onError}
    />
  );
  let body = <span className="welcome-popup-plain-link">{img}</span>;
  if (safeLink && internal && !newWindow) {
    body = <Link to={safeLink} className="welcome-popup-plain-link" onClick={onClose}>{img}</Link>;
  } else if (safeLink) {
    body = (
      <a href={safeLink} className="welcome-popup-plain-link" target="_blank" rel="noopener noreferrer" onClick={onClose}>
        {img}
      </a>
    );
  }
  return (
    <div className="welcome-popup-plain" onClick={(event) => event.stopPropagation()} data-bhs-popup-id={popupId}>
      {body}
      <button type="button" className="welcome-popup-plain-close" onClick={onClose} aria-label="Kapat">
        <X size={20} strokeWidth={2.5} />
      </button>
    </div>
  );
};

/** Betrun'da görselli pencere yalnız görseldir; görselsiz pencere kartta kalır. */
const plainCms = (popup) => (popup?.image_url ? {
  image: popup.image_url, alt: popup.alt_text || popup.title || '',
  link: popup.link || '', newWindow: Boolean(popup.new_window),
} : null);
const plainWelcome = (popup) => (popup?.image_url ? {
  image: popup.image_url, alt: popup.title || '', link: popup.button_url || '',
  newWindow: !isInternalLink(popup.button_url),
} : null);

/**
 * Siteye girene gösterilen karşılama penceresi (panel: Görünüm & Ayarlar →
 * Karşılama Penceresi).
 *
 * Oturum durumu bilinmeden karar verilmez: jetonu olan üye sayfayı
 * yenilediğinde `/auth/me` gelene kadar `user` boştur ve "yalnızca
 * ziyaretçilere" penceresi üyeye bir an görünürdü. Sayfa açıkken bir kez
 * gösterilir; kapatıldıktan sonra rota değişse de yeniden açılmaz.
 */
// Yalnız görselden oluşan pencere görsel inmeden gösterilmez (BETRUN 02.11.2026):
// pencere hemen açılınca yavaş mobil bağlantıda ilk girişte birkaç saniye boş ya
// da kırık bir kutu görünüyordu (görsel ~170 KB; ikinci girişte önbellekten gelir).
// Görsel inmezse ya da bu süre içinde inmezse pencere o girişte gösterilmez.
export const POPUP_IMAGE_WAIT_MS = 10000;

const WelcomePopup = () => {
  const settings = useSettings();
  const { user, ready } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [loadedImage, setLoadedImage] = useState('');
  const handled = useRef(0);
  const cmsPopups = settings.cms_popups;
  // Betrun şemasında CMS'te yayında açılır pencere yoksa eski karşılama
  // penceresi gösterilir (canlı Betrun böyle çalışır; panel: Karşılama
  // Penceresi). Diğer şemalarda bugünkü davranış korunur: `cms_popups`
  // dizisi geldiyse (boş olsa da) pencere yalnız CMS'ten seçilir.
  const betrun = settings.site_theme === 'betrun';
  const usesCms = Array.isArray(cmsPopups) && !(betrun && cmsPopups.length === 0);
  // Uyan bütün CMS pencereleri üst üste durur (26.10.2026): ilki önde, kapatılınca
  // sıradaki öne gelir. Kaydırmalı (slider) değildir.
  const [cmsQueue, setCmsQueue] = useState([]);
  const cmsPopup = cmsQueue[0] || null;
  // Sayfa açıkken CMS pencereleri bir kez seçilir: kapatılınca etki yeniden çalışır ve
  // "her seferinde" kaydı aynı anda yeniden seçerdi — oyuncu pencereyi kapatamıyordu
  // (BETRUN geçişi, 24.10.2026).
  const cmsShown = useRef(false);
  const popup = usesCms ? null : (settings.welcome_popup || null);
  const version = Number(popup?.version) || 0;

  // CMS kaydı varken pencere oradan seçilir (panel: CMS › Açılır pencereler).
  useEffect(() => {
    if (!ready || !usesCms || cmsPopup || cmsShown.current) return;
    const justRegistered = readJustRegistered();
    const chosen = pickPopups(cmsPopups, {
      user, pathname, mobile: isMobileViewport(), justRegistered, ...readPopupSeen(),
    });
    if (!chosen.length) return;
    cmsShown.current = true;
    if (chosen.some((item) => item.subtype === 'register')) clearJustRegistered();
    setCmsQueue(chosen);
  }, [cmsPopups, usesCms, ready, user, pathname, cmsPopup]);
  // "Görüldü" öne gelen pencereye yazılır: arkada bekleyen, oyuncu sayfayı
  // kapatırsa bir sonraki girişte yine gösterilir.
  useEffect(() => {
    if (cmsPopup) markPopupSeen(cmsPopup);
  }, [cmsPopup]);
  const closeCms = useCallback(() => setCmsQueue((queue) => queue.slice(1)), []);
  useEffect(() => {
    if (!cmsPopup) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') closeCms(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmsPopup, closeCms]);

  useEffect(() => {
    if (!ready || !popup) return undefined;
    if (handled.current === version) return undefined;
    const decision = shouldShowWelcomePopup(popup, {
      user, pathname, ...readWelcomeSeen(),
    });
    if (!decision) return undefined;
    const delay = Math.max(0, Number(popup.delay_seconds) || 0) * 1000;
    const timer = window.setTimeout(() => {
      handled.current = version;
      markWelcomeSeen(popup);
      setOpen(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [popup, version, ready, user, pathname]);

  const close = useCallback(() => setOpen(false), []);

  const waitingImage = (cmsPopup && betrun && plainCms(cmsPopup)?.image)
    || (open && popup && betrun && plainWelcome(popup)?.image) || '';
  useEffect(() => {
    if (!waitingImage || loadedImage === waitingImage) return undefined;
    const timer = window.setTimeout(() => (cmsPopup ? closeCms() : close()), POPUP_IMAGE_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [waitingImage, loadedImage, cmsPopup, closeCms, close]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (cmsPopup) {
    const plain = betrun ? plainCms(cmsPopup) : null;
    return (
      <div
        className="welcome-popup-root"
        data-bhs={HOOKS.WELCOME_POPUP}
        data-loading={plain && loadedImage !== plain.image ? '1' : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={cmsPopup.title || 'Duyuru'}
        onClick={closeCms}
      >
        <div className="welcome-popup-backdrop" />
        {/* Ekranda tek pencere durur (29.10.2026, sistem sahibi): arkada taşan
            kart yok; öndeki kapatılınca sıradaki gelir. Panelin özel JS'i
            "Daha sonra" ve avantaj satırını sayfadaki ilk pencereye ekler. */}
        <div className="welcome-popup-stack">
          {plain && (
            <PlainPopupCard
              key={cmsPopup.id}
              popupId={cmsPopup.id}
              {...plain}
              onClose={closeCms}
              onLoad={() => setLoadedImage(plain.image)}
              onError={closeCms}
            />
          )}
          {/* Betrun'da görselsiz, düğmeli pencere eski kartla çizilir. */}
          {!(betrun && plainCms(cmsPopup)) && (betrun && cmsAsWelcome(cmsPopup)
            ? <WelcomePopupCard key={cmsPopup.id} popup={cmsAsWelcome(cmsPopup)} onClose={closeCms} onAction={closeCms} scrollLayer />
            : <CmsPopupCard key={cmsPopup.id} popup={cmsPopup} onClose={closeCms} />)}
        </div>
      </div>
    );
  }

  // Pencere açıkken panelden kapatılırsa (ayarlar yenilenir) hemen kalkar.
  if (!open || !popup) return null;
  const plainW = betrun ? plainWelcome(popup) : null;

  return (
    <div
      className="welcome-popup-root"
      data-bhs={HOOKS.WELCOME_POPUP}
      data-loading={plainW && loadedImage !== plainW.image ? '1' : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={popup.title ? 'welcome-popup-title' : undefined}
      aria-label={popup.title ? undefined : 'Duyuru'}
      onClick={close}
    >
      <div className="welcome-popup-backdrop" />
      {plainW
        ? (
          <div className="welcome-popup-stack">
            <PlainPopupCard {...plainW} onClose={close} onLoad={() => setLoadedImage(plainW.image)} onError={close} />
          </div>
        )
        : <WelcomePopupCard popup={popup} onClose={close} onAction={close} scrollLayer={betrun} />}
    </div>
  );
};

export default WelcomePopup;
